import { Badge, Box, Flex, Heading, Text, VStack } from '@chakra-ui/react';
import { FileText, History } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

/**
 * Hero meta for the Terms & Conditions page (TZ-373). The V2 design uses a coral
 * LEGAL chip and a neutral last-updated chip, both separate from the consumer green brand.
 */
const LEGAL_BADGE = {
  bg: '#FFC2BB', // color-primary-200
  borderColor: '#FF5C4F', // color-primary-500
  color: '#921F15', // color-primary-800
};

const UPDATED_CHIP = {
  bg: '#F5F4F2', // surface-card
  borderColor: '#D6D2CD', // border-default
  color: '#79736B', // color-neutral-500
};

/** Last time the terms copy was revised. Western numerals + Arabic month to match the design. */
const LAST_UPDATED = new Intl.DateTimeFormat('ar-u-nu-latn', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
}).format(new Date(2026, 4, 6));

export const HeroSection = () => (
  <Box
    as='header'
    dir='rtl'
    bg='white'
    maxW='container.xl'
    mx='auto'
    px={{ base: '16px', md: '100px' }}
    pt={{ base: '24px', md: '40px' }}
    pb={{ base: '16px', md: '24px' }}
  >
    <VStack align='stretch' spacing={{ base: '12px', md: '16px' }}>
      <Badge
        alignSelf='flex-start'
        display='inline-flex'
        alignItems='center'
        justifyContent='center'
        w='125px'
        h='31px'
        gap='8px'
        bg={LEGAL_BADGE.bg}
        color={LEGAL_BADGE.color}
        borderWidth='1px'
        borderColor={LEGAL_BADGE.borderColor}
        borderRadius='9999px'
        px='12px'
        fontSize='12px'
        fontWeight='500'
        lineHeight='140%'
        letterSpacing='0.01em'
        textTransform='none'
      >
        <FileText size={14} aria-hidden />
        <FormattedMessage id='termsOfUse.legalBadge' defaultMessage='LEGAL' />
      </Badge>

      <Heading
        as='h1'
        m={0}
        color='#1B1917' // text-primary
        fontFamily="'Thmanyah Sans', 'Noto Sans Arabic', sans-serif"
        fontWeight='900'
        fontSize='32px'
        lineHeight='110%'
        letterSpacing='-0.02em'
      >
        <FormattedMessage id='termsOfUse.title' defaultMessage='Terms and Conditions' />
      </Heading>

      <Text color='dark.D500' textStyle='bodyLargeRegular' maxW='560px'>
        <FormattedMessage
          id='termsOfUse.intro'
          defaultMessage='By using the Ticket Zone platform to purchase tickets or register for an event, you automatically agree to all the following terms and conditions relating to customer service and platform policies.'
        />
      </Text>

      <Flex
        alignSelf='flex-start'
        align='center'
        justify='center'
        w={{ base: '173px', md: 'auto' }}
        h='24px'
        gap='8px'
        mt={{ base: '8px', md: '12px' }}
        bg={UPDATED_CHIP.bg}
        color={UPDATED_CHIP.color}
        borderWidth='1px'
        borderColor={UPDATED_CHIP.borderColor}
        borderRadius='9999px'
        pt='4px'
        pb='4px'
        pl='20px'
        pr='12px'
        whiteSpace='nowrap'
        textAlign='right'
        fontFamily="'Thmanyah Sans', 'Noto Sans Arabic', sans-serif"
        fontWeight='500'
        fontSize='11px'
        lineHeight='130%'
        letterSpacing='0.02em'
      >
        <History size={14} color='#FF5C4F' aria-hidden />
        <Box
          as='span'
          style={{
            color: UPDATED_CHIP.color,
            fontWeight: 'bold',
            fontSize: '11px',
            lineHeight: '130%',
            letterSpacing: '0.02em',
          }}
        >
          <FormattedMessage
            id='termsOfUse.lastUpdated'
            defaultMessage='Last updated: {date}'
            values={{ date: LAST_UPDATED }}
          />
          <Box as='span' display={{ base: 'none', md: 'inline' }}>
            {' · '}
            <FormattedMessage
              id='termsOfUse.lastUpdatedScope'
              defaultMessage='Applies to all transactions after this date'
            />
          </Box>
        </Box>
      </Flex>
    </VStack>
  </Box>
);
